<script lang="ts" setup>
import { Collapse } from 'bootstrap';
import { nextTick, onMounted, ref, watch } from 'vue';

const props = withDefaults(
    defineProps<{
        visible: boolean;
        removeContent?: boolean;
        horizontal?: boolean;
        usecustomTransition?: boolean;
    }>(),
    {
        removeContent: false,
        horizontal: false,
        usecustomTransition: false,
    },
);

const contentExists = ref(props.visible || !props.removeContent);

const collapseDiv = ref<HTMLElement | null>(null);

const hidden = () => (contentExists.value = false || !props.removeContent);
const show = () => (contentExists.value = true);

watch(
    () => props.visible,
    async () => {
        if (props.removeContent) {
            contentExists.value = props.visible;
            // We have to wait for the div to actually be rendered if we just made it exist, so we can get the collapse instance
            await nextTick();
        }

        if (!collapseDiv.value) {
            return;
        }

        // If we use a custom fadein fade out handler.
        // This is usually done by overriding the collapse-hide
        // and collapse-show css rule.
        // there is a basic version of this,
        if (props.usecustomTransition) return;

        const collapseInstance = Collapse.getOrCreateInstance(collapseDiv.value, { toggle: false });
        if (!collapseInstance) {
            return;
        }
        if (props.visible) {
            collapseInstance.show();
        } else {
            collapseInstance.hide();
        }
    },
);

// When we mount we have to check if the collapse should be initially visible.
onMounted(() => {
    if (!collapseDiv.value) {
        return;
    }
    if (props.visible) {
        collapseDiv.value?.classList.add('show');
    }
});
</script>

<template>
    <div
        class="collapse"
        :class="{ 'collapse-horizontal': horizontal, 'collapse-show': visible, 'collapse-hide': !visible, show: visible }"
        v-on="{ 'hidden.bs.collapse': hidden, 'show.bs.collapse': show }"
        ref="collapseDiv"
        v-if="contentExists"
    >
        <slot> </slot>
    </div>
</template>
